import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useParams } from 'react-router-dom'

import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'

const CODE_LENGTH = 5
const RESEND_TIMEOUT = 30

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    textAlign: 'center',
    paddingBottom: theme.spacing(1),
  },
  message: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  highlight: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
  },
  inputsWrapper: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  codeInput: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    textAlign: 'center',
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    outline: 'none',
    transition: theme.transitions.create(['border-color', 'box-shadow'], {
      duration: theme.transitions.duration.short,
    }),
    '&:focus': {
      borderColor: theme.palette.primary.main,
      boxShadow: `${theme.palette.primary.main}33 0px 0px 0px 4px`,
    },
    '&:disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
    },
  },
  resendWrapper: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  resendButton: {
    padding: 0,
    minWidth: 'auto',
    marginLeft: theme.spacing(0.5),
    textTransform: 'none',
  },
  dialogActions: {
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
  },
}))

const buildInputId = (index) => `complaint-two-factor-input-${index}`

const ComplaintTwoFactorDialog = ({
  accessToken,
  email,
  message,
  onCancel,
  onSuccess,
  open,
}) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { slug } = useParams()

  const [values, setValues] = useState(Array(CODE_LENGTH).fill(''))
  const [activeIndex, setActiveIndex] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [counter, setCounter] = useState(RESEND_TIMEOUT)
  const [canResend, setCanResend] = useState(false)

  useEffect(() => {
    if (!open) return

    setValues(Array(CODE_LENGTH).fill(''))
    setActiveIndex(0)
    setIsSubmitting(false)
    setIsResending(false)
    setCounter(RESEND_TIMEOUT)
    setCanResend(false)
  }, [open, accessToken])

  useEffect(() => {
    if (!open) return

    const input = document.getElementById(buildInputId(activeIndex))
    if (input) {
      input.focus()
      input.select()
    }
  }, [activeIndex, open])

  useEffect(() => {
    if (!open || canResend) return

    if (counter <= 0) {
      setCanResend(true)
      return
    }

    const timer = setTimeout(() => {
      setCounter((previous) => (previous > 0 ? previous - 1 : 0))
    }, 1000)

    return () => clearTimeout(timer)
  }, [open, counter, canResend])

  const code = useMemo(() => values.join(''), [values])
  const isCodeComplete =
    values.every((digit) => digit !== '') && code.length === CODE_LENGTH

  const defineErrorMessage = (error) => {
    const message = error?.response?.data?.error?.message

    if (message === 'não autorizado')
      return 'O código de autenticação é inválido ou expirou. Verifique-o e tente novamente.'

    return message || 'Ocorreu um erro ao validar o código. Tente novamente.'
  }

  const defineResendErrorMessage = (error) => {
    const message = error?.response?.data?.error?.message

    return message || 'Não foi possível reenviar o código. Tente novamente.'
  }

  const handleInputChange = (index) => (event) => {
    const { value } = event.target
    const sanitized = value.replace(/\D/g, '').slice(-1)

    setValues((previousValues) => {
      const nextValues = [...previousValues]
      nextValues[index] = sanitized || ''
      return nextValues
    })

    if (sanitized && index < CODE_LENGTH - 1) {
      setActiveIndex(index + 1)
    }
  }

  const handleKeyDown = (index) => (event) => {
    if (event.key !== 'Backspace') return

    setValues((previousValues) => {
      const nextValues = [...previousValues]

      if (nextValues[index]) {
        nextValues[index] = ''
        return nextValues
      }

      if (index === 0) return nextValues

      nextValues[index - 1] = ''
      setActiveIndex(index - 1)
      return nextValues
    })
  }

  const handleInputFocus = (index) => () => {
    setActiveIndex(index)
  }

  const handlePaste = (event) => {
    event.preventDefault()

    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH)

    if (!pasted) return

    const filledValues = Array(CODE_LENGTH)
      .fill('')
      .map((_, index) => pasted[index] || '')

    setValues(filledValues)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!accessToken) {
      snackbar.open({
        message:
          'Não foi possível validar o código. Recarregue a página e tente novamente.',
        variant: 'error',
      })
      return
    }

    if (!isCodeComplete) {
      snackbar.open({
        message: 'Informe todos os dígitos do código para continuar.',
        variant: 'error',
      })
      return
    }

    setIsSubmitting(true)

    try {
      const validateCodeResponse =
        await service.dponet.twoFactorAuthentication.validateCode(
          {
            accessToken,
            code,
          },
          'complaints',
        )

      const authToken = validateCodeResponse.data?.complaints_2faToken

      if (!authToken) {
        console.error('Resposta do validateCode:', validateCodeResponse.data)
        snackbar.open({
          message:
            'Token de autenticação não encontrado na resposta. Verifique o console para mais detalhes.',
          variant: 'error',
        })
        return
      }

      snackbar.open({
        message: 'Código validado com sucesso!',
        variant: 'success',
      })

      onSuccess({ authToken })
    } catch (error) {
      console.error('Erro ao validar código 2FA:', error)
      snackbar.open({
        message: defineErrorMessage(error),
        variant: 'error',
      })
      setValues(Array(CODE_LENGTH).fill(''))
      setActiveIndex(0)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (!accessToken || !canResend || isResending) return

    setIsResending(true)

    try {
      await service.dponet.twoFactorAuthentication.resendCode(
        {
          accessToken,
          ...(slug ? { publicPath: slug } : {}),
        },
        'complaints',
      )

      snackbar.open({
        message: 'O código foi reenviado. Verifique seu e-mail.',
        variant: 'success',
      })

      setCounter(RESEND_TIMEOUT)
      setCanResend(false)
    } catch (error) {
      snackbar.open({
        message: defineResendErrorMessage(error),
        variant: 'error',
      })
    } finally {
      setIsResending(false)
    }
  }

  const handleDialogClose = (_, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
    if (onCancel) onCancel()
  }

  const disableInputs = isSubmitting || !accessToken

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h4" color="textPrimary">
          Autenticação em duas etapas
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" className={classes.message}>
          {message ||
            'Enviamos um código de verificação no seu e-mail. Insira-o abaixo para continuar.'}
        </Typography>

        {email && (
          <Typography variant="body2" align="center">
            E-mail: <span className={classes.highlight}>{email}</span>
          </Typography>
        )}

        <Box className={classes.inputsWrapper}>
          {values.map((value, index) => (
            <input
              key={index}
              id={buildInputId(index)}
              className={classes.codeInput}
              value={value}
              maxLength={1}
              onChange={handleInputChange(index)}
              onKeyDown={handleKeyDown(index)}
              onFocus={handleInputFocus(index)}
              onPaste={handlePaste}
              disabled={disableInputs}
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
            />
          ))}
        </Box>

        <Typography variant="body2" className={classes.resendWrapper}>
          Não recebeu seu código?
          {isResending ? (
            <strong>&nbsp;Reenviando...</strong>
          ) : canResend ? (
            <Button
              className={classes.resendButton}
              color="primary"
              onClick={handleResend}
              variant="text"
              disabled={!accessToken || isResending}
            >
              Reenviar
            </Button>
          ) : (
            <strong>&nbsp;Reenvie em {counter}s</strong>
          )}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !isCodeComplete || !accessToken}
        >
          {isSubmitting ? 'Validando...' : 'Validar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ComplaintTwoFactorDialog.propTypes = {
  accessToken: PropTypes.string,
  context: PropTypes.object,
  email: PropTypes.string,
  message: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
}

ComplaintTwoFactorDialog.defaultProps = {
  accessToken: undefined,
  context: undefined,
  email: '',
  message: '',
}

export default ComplaintTwoFactorDialog
